import { useEffect, useMemo } from 'react';
import { IconButton } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { trackingIconPath } from '../models/TrackingIconForm';
import TrackingIconImage from './TrackingIconImage';

import { Badge, Table } from '@/components';
import { ColumnConfig } from '@/components/table/Table';
import { formatDate, timeAgo } from '@/utils';

import { useTrackingIconPagination, useTrackingIcons } from '../hooks';
import {
  TRACKING_ICON_SLOTS,
  TrackingIcon,
  TrackingIconFilter,
  TrackingIconSlot,
} from '../models';

type TrackingIconStatus =
  | 'Vigente'
  | 'Habilitado'
  | 'Programado'
  | 'Vencido'
  | 'Deshabilitado';

const statusColors: Record<
  TrackingIconStatus,
  { backgroundColor: string; ballColor: string; textColor: string }
> = {
  Habilitado: {
    backgroundColor: '#E3F2FD',
    ballColor: '#1976D2',
    textColor: '#1976D2',
  },
  Vigente: {
    backgroundColor: '#E8F5E9',
    ballColor: '#2E7D32',
    textColor: '#2E7D32',
  },
  Programado: {
    backgroundColor: '#E3F2FD',
    ballColor: '#1976D2',
    textColor: '#1976D2',
  },
  Vencido: {
    backgroundColor: '#FFF3E0',
    ballColor: '#ED6C02',
    textColor: '#ED6C02',
  },
  Deshabilitado: {
    backgroundColor: '#F3F4F6',
    ballColor: '#6B7280',
    textColor: '#4B5563',
  },
};

const getTrackingIconStatus = (trackingIcon: TrackingIcon) => {
  if (!trackingIcon.enabled) return 'Deshabilitado';
  if (trackingIcon.isDefault) return 'Habilitado';

  const now = Date.now();
  const dateFrom = trackingIcon.dateFrom
    ? new Date(trackingIcon.dateFrom).getTime()
    : null;
  const dateTo = trackingIcon.dateTo
    ? new Date(trackingIcon.dateTo).getTime()
    : null;

  if (dateFrom !== null && dateFrom > now) return 'Programado';
  if (dateTo !== null && dateTo <= now) return 'Vencido';

  return 'Vigente';
};

const formatValidityDate = (value: string | null, emptyLabel: string) =>
  value ? formatDate(value) : emptyLabel;

const columns: ColumnConfig[] = [
  {
    label: 'Imagen',
    field: 'imageUrl',
    render: (value: string | null | undefined, rowData: TrackingIcon) => (
      <TrackingIconImage imageUrl={value} name={rowData.name} />
    ),
  },
  { label: 'Nombre', field: 'name' },
  {
    label: 'Tipo',
    field: 'isDefault',
    render: (value: boolean) => (value ? 'Predeterminado' : 'Especial'),
  },
  {
    label: 'Vigente desde',
    field: 'dateFrom',
    render: (value: string | null, rowData: TrackingIcon) =>
      rowData.isDefault ? 'No aplica' : formatValidityDate(value, 'Sin inicio'),
  },
  {
    label: 'Vigente hasta',
    field: 'dateTo',
    render: (value: string | null, rowData: TrackingIcon) =>
      rowData.isDefault ? 'No aplica' : formatValidityDate(value, 'Sin fin'),
  },
  {
    label: 'Estado',
    field: 'enabled',
    render: (_value: boolean, rowData: TrackingIcon) => {
      const status = getTrackingIconStatus(rowData);
      return <Badge value={status} {...statusColors[status]} />;
    },
  },
  {
    label: 'Actualizado',
    field: 'updatedAt',
    render: (value: string, rowData: TrackingIcon) => (
      <div className='flex flex-col gap-1'>
        <time dateTime={value} title={formatDate(value)}>
          {timeAgo(value)}
        </time>
        <span className='text-xs text-gray-500'>
          {rowData.updatedByName
            ? `por ${rowData.updatedByName}`
            : 'Sin registro'}
        </span>
      </div>
    ),
  },
];

interface TrackingIconsTableProps {
  filter: TrackingIconFilter;
  slot: TrackingIconSlot;
}

const TrackingIconsTable = ({ filter, slot }: TrackingIconsTableProps) => {
  const navigate = useNavigate();
  const iconType =
    slot === TRACKING_ICON_SLOTS.destination ? 'destino' : 'repartidor';

  const filterKey = useMemo(
    () => [filter.name.trim(), filter.enabled, filter.isDefault].join('|'),
    [filter]
  );

  const { currentPage, pageSize, handlePageChange, handlePageSizeChange } =
    useTrackingIconPagination({ filterKey });

  const {
    trackingIcons,
    totalItems,
    totalPages,
    isLoading,
    isFetching,
    error,
  } = useTrackingIcons({
    filters: filter,
    slot,
    paginated: { page: currentPage, pageSize },
  });

  // Si se eliminan o deshabilitan íconos, podemos quedar en una página que
  // ya no existe; volvemos a la última disponible, o a la primera si queda vacía.
  useEffect(() => {
    if (isFetching || error) return;
    const lastPage = Math.max(totalPages, 1);
    if (currentPage > lastPage) {
      handlePageChange(lastPage);
    }
  }, [isFetching, error, totalPages, currentPage, handlePageChange]);

  const isTableLoading = isLoading || isFetching;

  return (
    <div className='w-full p-5 flex flex-col bg-white rounded-lg shadow-md gap-5 overflow-x-auto'>
      {error ? (
        <div className='text-center text-red-600 py-8'>
          Error al cargar los íconos de {iconType}. Por favor, intente
          nuevamente.
        </div>
      ) : trackingIcons.length > 0 ? (
        <Table
          data={trackingIcons}
          columns={columns}
          renderActions={(row: TrackingIcon) => (
            <IconButton
              aria-label={`Ver ícono ${row.name}`}
              onClick={() => navigate(`${trackingIconPath(slot)}/${row.id}`)}
            >
              <Visibility />
            </IconButton>
          )}
          isLoading={isTableLoading}
          pagination={{
            totalItems,
            totalPages,
            currentPage,
            rowsPerPage: pageSize,
            onPageChange: handlePageChange,
            onRowsPerPageChange: handlePageSizeChange,
          }}
        />
      ) : (
        <div className='text-center text-gray-500 py-8'>
          {isTableLoading
            ? `Cargando íconos de ${iconType}...`
            : `No hay íconos de ${iconType} por el momento`}
        </div>
      )}
    </div>
  );
};

export default TrackingIconsTable;
